'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_search', label: 'Search Saints', href: '/search-saints' },
    { id: 'nav_daily', label: 'Saint of the Day', href: '/saint-of-the-day' },
    { id: 'nav_blog', label: 'Blog', href: '/blog' },
  ];

  const isActive = (href: string) => pathname === href;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-3' : 'py-6'
      }`}
    >
      <nav
        className={`glass-card max-w-7xl mx-auto px-6 py-4 rounded-full transition-all duration-300 ${
          isScrolled ? 'shadow-lg' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center transition-transform group-hover:scale-110">
              <Icon name="SparklesIcon" size={24} className="text-white" />
            </div>
            <span className="text-xl font-serif font-bold text-foreground tracking-tight">
              SaintSeek
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-6">
              {navLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link?.href)
                      ? 'text-primary' :'text-foreground/70 hover:text-primary'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
            </div>
            {isAdmin && (
              <Link
                href="/admin/dashboard"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2"
              >
                <Icon name="cog-6-tooth" className="w-4 h-4" />
                Admin
              </Link>
            )}
          </div>

          {/* CTA & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Link
              href="/search-saints"
              className="hidden sm:flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="MagnifyingGlassIcon" size={16} />
              <span>Search Saints</span>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                className="text-foreground"
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-border/50">
            <div className="flex flex-col gap-3">
              {navLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-sm font-medium py-2 transition-colors ${
                    isActive(link?.href)
                      ? 'text-primary' :'text-foreground/70 hover:text-primary'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  href="/admin/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2 w-fit"
                >
                  <Icon name="cog-6-tooth" className="w-4 h-4" />
                  Admin
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}